package com.appsyde.shared.app.presentation.tickets

import com.appsyde.shared.app.presentation.inbox.ExtractOrchestrator
import com.appsyde.shared.core.presentation.BaseViewModel
import com.appsyde.shared.modules.ai.domain.model.AiExtractV2
import com.appsyde.shared.modules.ai.domain.model.parseKeysJsonToSet
import com.appsyde.shared.modules.ticket.domain.model.TicketRespondState
import com.appsyde.shared.modules.ticket.domain.usecase.GetTicketRespondStateUseCase
import com.appsyde.shared.modules.ticket.domain.model.Ticket
import com.appsyde.shared.modules.ticket.domain.model.TicketStatus
import com.appsyde.shared.modules.ticket.domain.model.ExtractStatus
import com.appsyde.shared.modules.ticket.domain.usecase.AssignUpToUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.ListTicketsUseCase
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json

class TicketsListViewModel(
    private val listTicketsUseCase: ListTicketsUseCase,
    private val getTicketRespondStateUseCase: GetTicketRespondStateUseCase,
    private val extractOrchestrator: ExtractOrchestrator,
    private val assignUpToUseCase: AssignUpToUseCase
) : BaseViewModel() {

    private val _state = MutableStateFlow(TicketsListState())
    val state: StateFlow<TicketsListState> = _state.asStateFlow()

    private val _selectedTicketId = MutableStateFlow<String?>(null)
    val selectedTicketId: StateFlow<String?> = _selectedTicketId.asStateFlow()

    private val _selectedRespond = MutableStateFlow<TicketRespondState?>(null)
    val selectedRespond: StateFlow<TicketRespondState?> = _selectedRespond.asStateFlow()

    private val _selectedExtract = MutableStateFlow<AiExtractV2?>(null)
    val selectedExtract: StateFlow<AiExtractV2?> = _selectedExtract.asStateFlow()

    private val _searchQuery = MutableStateFlow("")
    val searchQuery: StateFlow<String> = _searchQuery.asStateFlow()

    private val listPollIntervalMs: Long = 5_000L
    private var listPollJob: Job? = null

    private val json = Json { ignoreUnknownKeys = true }

    fun setSearchQuery(query: String) {
        _searchQuery.value = query
    }

    init {
        viewModelScope.launch {
            extractOrchestrator.extractByTicketId.collectLatest { sharedExtracts ->
                _state.update { it.copy(extractByTicketId = sharedExtracts) }
                val selectedId = _selectedTicketId.value
                if (selectedId != null) {
                    _selectedExtract.value = sharedExtracts[selectedId]
                }
            }
        }
    }

    /**
     * Entry point for app startup: load list, merge extracts from API (when READY), signal
     * initialLoadDone immediately, then start polling so titles update when extracts complete.
     * If user has no assigned tickets, auto-assigns up to 2 from available pool, then loads list.
     */
    fun loadFirstScreen() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }

            runCatching {
                runCatching { assignUpToIfNoneAssigned() }
                applyListFromApi(listTicketsUseCase(limit = 100))
                _state.update { it.copy(isLoading = false, initialLoadDone = true) }
                startListPolling()
            }.onFailure {
                _state.update {
                    it.copy(isLoading = false, initialLoadDone = true)
                }
            }
        }
    }

    /**
     * Refresh list (e.g. user pull-to-refresh). Merges extracts from API when READY.
     * If user has no assigned tickets, auto-assigns up to 2 from available pool, then loads list.
     */
    fun load() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }
            runCatching {
                runCatching { assignUpToIfNoneAssigned() }
                applyListFromApi(listTicketsUseCase(limit = 100))
                _state.update { it.copy(isLoading = false) }
            }.onFailure {
                _state.update { it.copy(isLoading = false) }
            }
        }
    }

    /** If the user has no assigned tickets, assigns up to 2 from unassigned pool (best-effort). */
    private suspend fun assignUpToIfNoneAssigned() {
        val myAssigned = listTicketsUseCase(limit = 1, assignedToMe = true)
        if (myAssigned.isEmpty()) {
            assignUpToUseCase(2)
        }
    }

    /** Background poll: refetch list periodically so titles update when backend finishes extracts. */
    private fun startListPolling() {
        if (listPollJob?.isActive == true) return
        listPollJob = viewModelScope.launch {
            while (true) {
                delay(listPollIntervalMs)
                runCatching {
                    refreshListFromPoll()
                }
            }
        }
    }

    /** Refetch list without loading indicator; merge new extracts into cache so row titles update. */
    private suspend fun refreshListFromPoll() {
        val list = listTicketsUseCase(limit = 100)
        applyListFromApi(list)
    }

    /** Sort order: plus récent en premier, puis ticketId pour ordre stable quand updatedAtMs égal. */
    private fun sortTicketsByUpdatedDesc(tickets: List<Ticket>): List<Ticket> =
        tickets.sortedWith(compareByDescending<Ticket> { it.updatedAtMs }.thenBy { it.ticketId })

    /**
     * Splits list into open/waiting/closed, sorts each by updatedAtMs DESC so order is stable
     * (e.g. ticket just changed to "En attente" stays at top of its category after refresh).
     */
    private fun applyListFromApi(list: List<Ticket>) {
        val open = sortTicketsByUpdatedDesc(list.filter { it.status.equals(TicketStatus.OPEN, ignoreCase = true) })
        val waiting = sortTicketsByUpdatedDesc(list.filter { it.status.equals(TicketStatus.WAITING, ignoreCase = true) })
        val closed = sortTicketsByUpdatedDesc(list.filter { it.status.equals(TicketStatus.CLOSED, ignoreCase = true) })

        for (t in list) {
            if (t.extractStatus == ExtractStatus.READY && !t.extractJson.isNullOrBlank()) {
                runCatching {
                    json.decodeFromString(AiExtractV2.serializer(), t.extractJson)
                }.onSuccess { extract ->
                    extractOrchestrator.putExtract(t.ticketId, extract)
                }
            }
        }

        _state.update {
            it.copy(
                openTickets = open,
                waitingTickets = waiting,
                closedTickets = closed,
                extractByTicketId = extractOrchestrator.snapshotExtracts(),
                initialLoadDone = it.initialLoadDone
            )
        }
    }

    fun selectTicket(ticketId: String) {
        _selectedTicketId.value = ticketId
        loadRespondAndExtractForSelection(ticketId)
    }

    private fun toKeysJson(keys: Set<String>): String {
        if (keys.isEmpty()) return "[]"
        return keys.sorted().joinToString(prefix = "[\"", separator = "\",\"", postfix = "\"]")
    }

    private fun mutateTicketLocally(ticketId: String, transform: (Ticket) -> Ticket) {
        _state.update { s ->
            val current = s.openTickets
                .plus(s.waitingTickets)
                .plus(s.closedTickets)
                .firstOrNull { it.ticketId == ticketId } ?: return@update s

            val updated = transform(current)
            val open = s.openTickets.filterNot { it.ticketId == ticketId }.toMutableList()
            val waiting = s.waitingTickets.filterNot { it.ticketId == ticketId }.toMutableList()
            val closed = s.closedTickets.filterNot { it.ticketId == ticketId }.toMutableList()

            when (updated.status.uppercase()) {
                TicketStatus.OPEN -> open.add(updated)
                TicketStatus.WAITING -> waiting.add(updated)
                TicketStatus.CLOSED -> closed.add(updated)
                else -> open.add(updated)
            }

            s.copy(
                openTickets = sortTicketsByUpdatedDesc(open),
                waitingTickets = sortTicketsByUpdatedDesc(waiting),
                closedTickets = sortTicketsByUpdatedDesc(closed)
            )
        }

        _selectedRespond.update { respond ->
            if (respond?.ticket?.ticketId == ticketId) {
                respond.copy(ticket = transform(respond.ticket))
            } else {
                respond
            }
        }
    }

    /**
     * Applies an immediate local status update so list UI stays in sync with Respond tab
     * without waiting for a full reload.
     */
    fun updateTicketStatusLocally(ticketId: String, newStatus: String) {
        val normalizedStatus = when (newStatus.trim().uppercase()) {
            TicketStatus.OPEN -> TicketStatus.OPEN
            TicketStatus.WAITING -> TicketStatus.WAITING
            TicketStatus.CLOSED -> TicketStatus.CLOSED
            else -> return
        }

        mutateTicketLocally(ticketId) { it.copy(status = normalizedStatus) }
    }

    /**
     * Applies local update when a missing key is filled in Respond.
     */
    fun removeMissingKeyLocally(ticketId: String, key: String) {
        val keyNorm = key.trim().lowercase()
        if (keyNorm.isBlank()) return
        mutateTicketLocally(ticketId) { t ->
            val nextMissing = parseKeysJsonToSet(t.missingKeysJson) - keyNorm
            t.copy(missingKeysJson = toKeysJson(nextMissing))
        }
    }

    /**
     * Applies local update when a to-confirm key is validated in Respond.
     */
    fun removeToConfirmKeyLocally(ticketId: String, key: String) {
        val keyNorm = key.trim().lowercase()
        if (keyNorm.isBlank()) return
        mutateTicketLocally(ticketId) { t ->
            val nextToConfirm = parseKeysJsonToSet(t.toConfirmKeysJson) - keyNorm
            t.copy(toConfirmKeysJson = toKeysJson(nextToConfirm))
        }
    }

    fun selectTicketById(ticketId: String?) {
        if (ticketId == null) {
            _selectedTicketId.value = null
            _selectedRespond.value = null
            _selectedExtract.value = null
            return
        }
        _selectedTicketId.value = ticketId
        loadRespondAndExtractForSelection(ticketId)
    }

    private fun ticketFromList(ticketId: String): Ticket? {
        val s = _state.value
        return s.openTickets.plus(s.waitingTickets).plus(s.closedTickets).firstOrNull { it.ticketId == ticketId }
    }

    private fun loadRespondAndExtractForSelection(ticketId: String) {
        viewModelScope.launch {
            runCatching {
                getTicketRespondStateUseCase(ticketId)
            }.onSuccess { respond ->
                if (_selectedTicketId.value != ticketId) return@launch
                _selectedRespond.value = respond
                val cachedExtract = extractOrchestrator.getCachedExtract(ticketId)
                if (cachedExtract != null) {
                    _selectedExtract.value = cachedExtract
                } else {
                    _selectedExtract.value = null
                    respond.lastInboundMessageId?.let { msgId ->
                        launch {
                            runCatching {
                                extractOrchestrator.ensureExtract(
                                    ticketId = ticketId,
                                    messageId = msgId,
                                    force = false
                                )
                            }.onSuccess { extract ->
                                if (_selectedTicketId.value == ticketId) {
                                    _selectedExtract.value = extract
                                    extractOrchestrator.putExtract(ticketId, extract)
                                }
                            }
                        }
                    }
                }
            }.onFailure {
                val ticket = ticketFromList(ticketId)
                if (ticket != null) {
                    _selectedRespond.value = TicketRespondState(
                        ticket = ticket,
                        messages = emptyList(),
                        draft = null,
                        lastInboundMessageId = ticket.lastInboundMessageId
                    )
                    _selectedExtract.value = _state.value.extractByTicketId[ticketId]
                } else {
                    _selectedRespond.value = null
                    _selectedExtract.value = null
                }
            }
        }
    }
}
